import { useNavigate } from "react-router-dom";
import { useState } from "react";

// Data
import { createUserWithEmailAndPassword } from "firebase/auth";
import { doc, setDoc } from "firebase/firestore";
import { db, auth } from "../../firebase";

// Styles
import {
  Stack,
  Box,
  Typography,
  Checkbox,
  FormControlLabel,
  FormGroup,
  Button,
  InputAdornment,
} from "@mui/material";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import theme from "../../theme";
import { sharableInputLabelStyles } from "../../ui/sharableStyles";

// Formik
import { Formik, Form, Field } from "formik";
import { TextField } from "formik-mui";
import * as yup from "yup";
import { FaLink } from "react-icons/fa6";
import axios from "axios";
import urls from "../../constants/urls";
import { loginWithEmail } from "../../helpers/loginUser";
import { signupWithEmail } from "../../helpers/signupWithEmail";
import { useAppDispatch } from "../../app/store";

const validationSchema = yup.object({
  firstName: yup
    .string()
    .trim()
    .matches(
      /^[\p{L}\p{M}-]+$/u,
      "First name cannot contain numbers or special characters"
    )
    .min(2, "First name should be of minimum 2 characters length")
    .required("First name is required"),
  lastName: yup
    .string()
    .trim()
    .matches(
      /^[\p{L}\p{M}-]+$/u,
      "Last name cannot contain numbers or special characters"
    )
    .min(2, "Last name should be of minimum 2 characters length")
    .required("Last name is required"),
  email: yup
    .string()
    .email("Enter a valid email")
    .required("Email is required"),
  password: yup
    .string()
    .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required"),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref("password"), null], "Passwords must match")
    .min(8, "Password should be of minimum 8 characters length")
    .required("Password is required"),
});

interface IinitialValues {
  firstName: string;
  lastName: string;
  email: string;
  password: string;
  confirmPassword: string;
}

const initialValues: IinitialValues = {
  firstName: "",
  lastName: "",
  email: "",
  password: "",
  confirmPassword: "",
};

const Register = () => {
  const [loading, setLoading] = useState(false);
  const [isPasswordShown, setIsPasswordShown] = useState({
    password: false,
    confirmPassword: false,
  });
  const [error, setErrorMessage] = useState("");

  const handleClickPasswordIcon = (key: string) => {
    if (key === "password") {
      setIsPasswordShown((state) => ({ ...state, password: !state.password }));
    } else if (key === "confirmPassword") {
      setIsPasswordShown((state) => ({
        ...state,
        confirmPassword: !state.confirmPassword,
      }));
    }
  };
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const handleRegisterUser = async (values: IinitialValues) => {
    setLoading(true);
    signupWithEmail(
      {
        email: values.email,
        password: values.password,
        first_name: values.firstName,
        last_name: values.lastName,
      },
      {
        dispatch,
        navigate,
        setErrorMessage,
        setLoading,
      }
    );
  };

  const handleClickSignIn = () => {
    navigate({ pathname: "/auth/login" });
  };

  return (
    <Stack
      alignItems="center"
      sx={{
        padding: "16px",
        "& form": {
          display: "flex",
          flexDirection: "column",
          rowGap: "15px",
          width: "100%",
          maxWidth: "448px",
        },
        "& .MuiOutlinedInput-root": {
          borderRadius: "56px",
          borderColor: "#D8D8D8",
        },
      }}
    >
      <Typography
        sx={{
          fontSize: "22px",
          fontWeight: theme.fontWeight.semiBold,
          marginBottom: "24px",
          textAlign: "center",
        }}
      >
        Create an account
      </Typography>

      <Typography
        sx={{
          color: "#7D7D7D",
          fontSize: "13px",
          fontWeight: theme.fontWeight.regular,
          lineHeight: "25px",
          maxWidth: "358px",
          textAlign: "center",
          marginBottom: "40px",
        }}
      >
        Create an account and check out faster, track orders, and be up to date
        with the latest news!
      </Typography>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={handleRegisterUser}
      >
        {() => (
          <Form id="registerForm" noValidate>
            <Field
              disabled={loading}
              component={TextField}
              type="text"
              name="firstName"
              label="First Name"
              sx={{ ...sharableInputLabelStyles }}
            />
            <Field
              disabled={loading}
              component={TextField}
              type="text"
              name="lastName"
              label="Last Name"
              sx={{ ...sharableInputLabelStyles }}
            />
            <Field
              disabled={loading}
              component={TextField}
              type="email"
              name="email"
              label="Email"
              sx={{ ...sharableInputLabelStyles }}
            />
            <Field
              disabled={loading}
              component={TextField}
              type={isPasswordShown.password ? "text" : "password"}
              name="password"
              label="Password"
              sx={{ ...sharableInputLabelStyles }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                    {isPasswordShown.password ? (
                      <Visibility
                        onClick={() => handleClickPasswordIcon("password")}
                      />
                    ) : (
                      <VisibilityOff
                        onClick={() => handleClickPasswordIcon("password")}
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <Field
              disabled={loading}
              component={TextField}
              type={isPasswordShown.confirmPassword ? "text" : "password"}
              name="confirmPassword"
              label="Confirm Password"
              sx={{ ...sharableInputLabelStyles }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end" sx={{ cursor: "pointer" }}>
                    {isPasswordShown.confirmPassword ? (
                      <Visibility
                        onClick={() =>
                          handleClickPasswordIcon("confirmPassword")
                        }
                      />
                    ) : (
                      <VisibilityOff
                        onClick={() =>
                          handleClickPasswordIcon("confirmPassword")
                        }
                      />
                    )}
                  </InputAdornment>
                ),
              }}
            />
            <FormGroup
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                justifyContent: "space-between",
                "& label": { width: "max-content", marginRight: "0" },
              }}
            >
              <Field
                disabled={loading}
                component={FormControlLabel}
                control={<Checkbox />}
                label="I agree to the M-jenzi Terms of Service and Privacy Policy"
                sx={{
                  "& span": {
                    fontSize: "13px",
                    fontWeight: theme.fontWeight.regular,
                  },
                }}
              />
            </FormGroup>
          </Form>
        )}
      </Formik>

      <Button
        variant="contained"
        type="submit"
        form="registerForm"
        disabled={loading}
        sx={{
          backgroundColor: "#FBB03B",
          borderRadius: "56px",
          display: "flex",
          alignItems: "center",
          fontSize: "13px",
          fontWeight: theme.fontWeight.semiBold,
          marginTop: "20px",
          maxWidth: { xs: "400px", sm: "448px" },
          textAlign: "center",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#ffb53d",
          },
          height: "56px",
          width: "100%",
        }}
      >
        Sign up
      </Button>
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          columnGap: "30px",
          marginTop: "22px",
        }}
      >
        <Typography
          sx={{ fontSize: "14px", fontWeight: "300", cursor: "pointer" }}
        >
          Are you already a member?
        </Typography>
        <Typography
          sx={{ fontSize: "14px", fontWeight: "500", cursor: "pointer" }}
          onClick={handleClickSignIn}
        >
          Sign in
        </Typography>
      </Box>
      <Button
        variant="contained"
        href="https://vendor.m-jenzi.com"
        sx={{
          backgroundColor: "#FBB03B",
          borderRadius: "56px",
          display: "flex",
          alignItems: "center",
          fontSize: "13px",
          fontWeight: theme.fontWeight.semiBold,
          marginTop: "20px",
          maxWidth: { xs: "400px", sm: "448px" },
          textAlign: "center",
          textTransform: "none",
          "&:hover": {
            backgroundColor: "#ffb53d",
          },
          height: "56px",
          width: "100%",
        }}
        // add the external link icon imported from react icons
        endIcon={<FaLink />}
      >
        Vendor Registration
      </Button>
    </Stack>
  );
};

export default Register;
